import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { UTILS } from 'endpointsConst';

interface LookupOption {
  value: string | number;
  label: string;
}

interface RootState {
  settings: {
    lang: string;
  };
}

interface UseSearchLookupsReturn {
  bodyTypes: LookupOption[];
  loadingTypes: LookupOption[];
  paymentTypes: LookupOption[];
  loading: boolean;
  getBodyTypeLabel: (ids: (number | string)[] | number | string | undefined, maxVisible?: number) => string | null;
  getLoadingTypeLabels: (ids: (number | string)[] | undefined, maxVisible?: number) => string | null;
  getPaymentTypeLabels: (ids: (number | string)[] | undefined) => string | null;
}

export const useSearchLookups = (): UseSearchLookupsReturn => {
  const lang = useSelector((state: RootState) => state.settings.lang);
  const [bodyTypes, setBodyTypes] = useState<LookupOption[]>([]);
  const [loadingTypes, setLoadingTypes] = useState<LookupOption[]>([]);
  const [paymentTypes, setPaymentTypes] = useState<LookupOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchLookups = async () => {
      setLoading(true);
      try {
        const [bodyTypesRes, loadingTypesRes, paymentTypesRes] = await Promise.all([
          axios.get(`${UTILS}/transport-bodytypes/?lang=${lang}`),
          axios.get(`${UTILS}/loadingTypes/?lang=${lang}`),
          axios.get(`${UTILS}/paymentTypes/?lang=${lang}`),
        ]);

        setBodyTypes(
          bodyTypesRes.data.map((item: { id: number; value: string }) => ({
            value: item.id,
            label: item.value,
          }))
        );
        setLoadingTypes(
          loadingTypesRes.data.map((item: { id: number; value: string }) => ({
            value: item.id,
            label: item.value,
          }))
        );
        setPaymentTypes(
          paymentTypesRes.data.map((item: { id: number; value: string }) => ({
            value: item.id,
            label: item.value,
          }))
        );
      } catch (error) {
        console.error('Failed to fetch lookups:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLookups();
  }, [lang]);

  const getBodyTypeLabel = useCallback(
    (ids: (number | string)[] | number | string | undefined, maxVisible: number = 2): string | null => {
      if (!ids) return null;
      // Handle both single ID and array of IDs
      const idsArray = Array.isArray(ids) ? ids : [ids];
      if (idsArray.length === 0) return null;
      const labels = idsArray
        .map((id) => {
          return bodyTypes.find((item) => String(item.value) === String(id))?.label;
        })
        .filter(Boolean) as string[];
      if (labels.length === 0) return null;

      // Truncate if too many
      const hasMore = labels.length > maxVisible;
      const visibleLabels = labels.slice(0, maxVisible);
      const remainingCount = labels.length - maxVisible;

      return visibleLabels.join(', ') + (hasMore ? ` +${remainingCount}` : '');
    },
    [bodyTypes]
  );

  const getLoadingTypeLabels = useCallback(
    (ids: (number | string)[] | undefined, maxVisible: number = 3): string | null => {
      if (!ids || !Array.isArray(ids) || ids.length === 0) return null;
      const labels = ids
        .map((id) => {
          return loadingTypes.find((item) => String(item.value) === String(id))?.label;
        })
        .filter(Boolean) as string[];
      if (labels.length === 0) return null;

      const hasMore = labels.length > maxVisible;
      const visibleLabels = labels.slice(0, maxVisible);
      const remainingCount = labels.length - maxVisible;

      return visibleLabels.join(', ') + (hasMore ? ` +${remainingCount}` : '');
    },
    [loadingTypes]
  );

  const getPaymentTypeLabels = useCallback(
    (ids: (number | string)[] | undefined): string | null => {
      if (!ids || !Array.isArray(ids) || ids.length === 0) return null;
      const labels = ids
        .map((id) => {
          return paymentTypes.find((item) => String(item.value) === String(id))?.label;
        })
        .filter(Boolean);
      return labels.length > 0 ? labels.join(', ') : null;
    },
    [paymentTypes]
  );

  return {
    bodyTypes,
    loadingTypes,
    paymentTypes,
    loading,
    getBodyTypeLabel,
    getLoadingTypeLabels,
    getPaymentTypeLabels,
  };
};
